import moment from 'moment-timezone';
import { STRING_UOM } from '@/main/app.constants';
import { INTERPOLATION_TYPES } from '@/tools/manualSignal/manualSignal.constants';
import type { ManualSample } from '@/tools/manualSignal/manualSignal.constants';
import type { ManualSignalState } from '@/tools/manualSignal/manualSignal.store';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

type FormulaState = Pick<ManualSignalState, 'interpolationMethod' | 'maximumInterpolation' | 'samples' | 'uom'>;

export function getSampleFormula(sample: ManualSample, uom: string | undefined, isStringSignal: boolean): string {
  const value = isStringSignal ? `'${sample.value}'` : `${sample.value}${uom === undefined ? '' : uom}`;
  return `sample('${moment(sample.dateTime).toISOString()}', ${value})`;
}

export function generateFormula({ interpolationMethod, maximumInterpolation, samples, uom }: FormulaState): string {
  if (samples.length === 0) return '';
  const isStringSignal = uom === STRING_UOM || Number.isNaN(Number(samples[0].value));
  const samplesForFormula = samples.map((sample) => getSampleFormula(sample, uom, isStringSignal)).join(',\n');
  const maxInterp =
    interpolationMethod === INTERPOLATION_TYPES.DISCRETE
      ? '0h'
      : `${maximumInterpolation.value}${maximumInterpolation.units}`;

  return `signal(InterpolationMethod.${interpolationMethod}, ${maxInterp},\n${samplesForFormula})`;
}

/**
 * Converts a maximum interpolation form value into a storable value, or `undefined` when the input is incomplete or
 * invalid and should be ignored.
 */
export function toValidMaximumInterpolation(input: {
  value?: number | string;
  units?: string;
  valid?: boolean;
}): ValueWithUnitsItem | undefined {
  if (input.valid === false || input.value == null || input.units == null || Number.isNaN(Number(input.value))) {
    return undefined;
  }
  return { value: Number(input.value), units: input.units };
}
